import {
  BG_STYLES,
  BORDER_STYLES,
  COMMON_STYLES,
  LazyImage,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import React, { useEffect, useRef, useState } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  Text,
  View,
  StyleSheet,
  Image,
  Pressable,
} from "react-native";
import { Crop, DiseaseDetailProps } from "../models/diagnosis";
import { FallbackIcon } from "./RoundImageWithIcon";
import { LEAF } from "../assests/images";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { useAppSelector } from "app/hooks/reduxHooks";
import { selectPlantixUId } from "app/farmer360/store/app";
import { useTranslation } from "react-i18next";

export interface Props {
  disease: DiseaseDetailProps;
  cropId: number;
  cropName: string;
  onImagePress?: (item: string[]) => void;
}

const DiseaseDetail = ({ disease, onImagePress, cropId, cropName }: Props) => {
  const { t } = useTranslation();
  const scrollRef = useRef<ScrollView>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(
      event.nativeEvent.contentOffset.x / containerWidth
    );
    setActiveIndex(index);
  };
  const plantixId = useAppSelector(selectPlantixUId);
  const onLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width - 32);
  };

  useEffect(() => {
    if (disease) {
      trackEvent(CustomEventTypes.CropDocImageUserLandedOnDiseaseDetail, {
        PlantixID: plantixId,
        CropID: cropId,
        CropName: cropName,
        DiseaseID: disease.disease_id,
        DiseaseName: disease.disease_name,
      });
    }
  }, [disease]);

  return (
    <>
      <View style={[COMMON_STYLES.p1, BG_STYLES.bgWhite]} onLayout={onLayout}>
        <Text
          style={[
            TYPOGRAPHY.HEADING1,
            VARIANTS_STYLES.EMPHASIZED,
            COMMON_STYLES["mb1/4"],
          ]}
        >
          {disease?.disease_name}
        </Text>
        <Text
          style={[
            TYPOGRAPHY.TEXT1,
            VARIANTS_STYLES.SEMI_EMPHASIZED,
            COMMON_STYLES["mb0.5"],
          ]}
        >
          {disease?.pathogen}
        </Text>
        {containerWidth > 0 && (
          <Pressable
            onPress={() => {
              onImagePress?.(disease.image_refs);
              trackEvent(CustomEventTypes.CropDocImageViewed, {
                PlantixID: plantixId,
              });
            }}
          >
            <ScrollView
              ref={scrollRef}
              horizontal
              pagingEnabled
              onScroll={handleScroll}
              scrollEventThrottle={16}
              showsHorizontalScrollIndicator={false}
              style={[styles.scrollView, { width: containerWidth }]}
            >
              {disease?.image_refs.map((image, index) => (
                <LazyImage
                  imageUrl={image}
                  fallBackIcon={
                    <FallbackIcon
                      text={disease?.disease_name}
                      containerStyle={[styles.image, { width: containerWidth }]}
                    />
                  }
                  imgStyles={[styles.image, { width: containerWidth }]}
                />
              ))}
            </ScrollView>

            <View style={styles.dotsContainer}>
              {disease?.image_refs.map((_, index) => (
                <View
                  key={index}
                  style={[
                    styles.dot,
                    activeIndex === index && styles.activeDot,
                  ]}
                />
              ))}
            </View>
          </Pressable>
        )}
        <Text
          style={[
            TYPOGRAPHY.CAPTION1,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral80,
            COMMON_STYLES.textRight,
          ]}
        >
          {disease?.image_refs?.length} photos
        </Text>
      </View>
      <View
        style={[
          COMMON_STYLES.p1,
          BG_STYLES.bgWhite,
          { borderTopWidth: 16 },
          BORDER_STYLES.borderNeutral10,
        ]}
      >
        <View
          style={[
            COMMON_STYLES.p1,
            BG_STYLES.bgNeutral10,
            COMMON_STYLES.b1,
            COMMON_STYLES.br1,
            BORDER_STYLES.borderNeutral20,
          ]}
        >
          <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}>
            <Image
              source={LEAF}
              style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
            />
            <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
              {t("symptoms")}
            </Text>
          </View>
          {disease?.symptoms.map((symptoms) => {
            return (
              <View
                style={[
                  COMMON_STYLES.row,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES["mb3/4"],
                ]}
              >
                <Text style={{ fontSize: 20, lineHeight: 24, marginRight: 6 }}>
                  •
                </Text>
                <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
                  {symptoms}
                </Text>
              </View>
            );
          })}
          <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
            {disease?.summary}
          </Text>
        </View>
      </View>
      <View
        style={[
          COMMON_STYLES.p1,
          BG_STYLES.bgWhite,
          { borderTopWidth: 16 },
          BORDER_STYLES.borderNeutral10,
        ]}
      >
        <View
          style={[
            COMMON_STYLES.p1,
            BG_STYLES.bgNeutral10,
            COMMON_STYLES.b1,
            COMMON_STYLES.br1,
            BORDER_STYLES.borderNeutral20,
          ]}
        >
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              COMMON_STYLES["mb3/4"],
            ]}
          >
            <Image
              source={LEAF}
              style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
            />
            <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
              {t("what_cause_it")}
            </Text>
          </View>

          <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
            {disease?.trigger}
          </Text>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  editButtonContainer: {
    right: 10,
  },
  scrollView: {
    height: 200,
  },
  image: {
    height: 200,
    borderRadius: 12,
    marginRight: 8,
  },
  dotsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#ccc",
    marginHorizontal: 4,
  },
  activeDot: {
    backgroundColor: "#000",
  },
});

export default DiseaseDetail;
